import { pickWritableFile } from "./fileSystemAccess";

// ~1MB per chunk (character count, mostly ASCII JSON so ~1 byte/char).
const DEFAULT_CHUNK_SIZE = 1_000_000;

export type JsonGenerationProgress = {
  /** Cumulative count of array/object/leaf nodes visited so far — pair with `countJsonNodes` for a % complete. */
  nodesWritten: number;
  /** Cumulative JSON character count produced so far. */
  charsWritten: number;
};

/**
 * Pretty-prints `value` as JSON, yielding string chunks lazily instead of building one JSON
 * string. `JSON.stringify` has to materialize the whole output as one JS string, which hits the
 * engine's max string length (~512MB-1GB) for very large payloads; chunking avoids that ceiling
 * since no individual string ever gets that big. Because this is a generator, a caller that
 * writes each chunk out as it arrives (see `writeJsonToFile`) never has to hold the full
 * serialized payload in memory at once either.
 *
 * `onProgress`, if given, is called once per emitted chunk (not per node — that'd be too frequent
 * to be a useful UI signal on large reports) with cumulative totals. It fires for any consumer of
 * this generator, so it works the same way whether the chunks end up written straight to disk or
 * collected into a Blob.
 */
export function* generateJsonChunks(
  value: unknown,
  indentSize = 2,
  chunkSize = DEFAULT_CHUNK_SIZE,
  onProgress?: (progress: JsonGenerationProgress) => void,
): Generator<string, void, unknown> {
  let current = "";
  let charsWritten = 0;
  let nodesWritten = 0;

  function* write(str: string): Generator<string, void, unknown> {
    current += str;
    charsWritten += str.length;
    if (current.length >= chunkSize) {
      onProgress?.({ nodesWritten, charsWritten });
      yield current;
      current = "";
    }
  }

  const indent = (level: number) => " ".repeat(level * indentSize);

  // Ancestors currently on the recursion stack — the same object reachable via two different
  // branches (e.g. a shared GraphNode) is fine and matches JSON.stringify, but an object nested
  // inside itself must throw immediately rather than recurse until the stack/heap gives out.
  const ancestors = new Set<object>();

  function* walk(val: unknown, level: number): Generator<string, void, unknown> {
    nodesWritten++;

    if (val && typeof val === "object" && typeof (val as { toJSON?: unknown }).toJSON === "function") {
      val = (val as { toJSON: () => unknown }).toJSON();
    }

    if (val === null || typeof val === "number" || typeof val === "boolean" || typeof val === "string") {
      yield* write(JSON.stringify(val));
      return;
    }

    if (Array.isArray(val)) {
      if (ancestors.has(val)) throw new TypeError("Converting circular structure to JSON");
      if (val.length === 0) {
        yield* write("[]");
        return;
      }
      ancestors.add(val);
      yield* write("[\n");
      for (let i = 0; i < val.length; i++) {
        const item = val[i];
        yield* write(indent(level + 1));
        yield* walk(item === undefined ? null : item, level + 1);
        yield* write(i < val.length - 1 ? ",\n" : "\n");
      }
      yield* write(`${indent(level)}]`);
      ancestors.delete(val);
      return;
    }

    if (typeof val === "object") {
      if (ancestors.has(val)) throw new TypeError("Converting circular structure to JSON");
      const entries = Object.entries(val).filter(([, v]) => v !== undefined);
      if (entries.length === 0) {
        yield* write("{}");
        return;
      }
      ancestors.add(val);
      yield* write("{\n");
      for (let i = 0; i < entries.length; i++) {
        const [key, v] = entries[i];
        yield* write(`${indent(level + 1)}${JSON.stringify(key)}: `);
        yield* walk(v, level + 1);
        yield* write(i < entries.length - 1 ? ",\n" : "\n");
      }
      yield* write(`${indent(level)}}`);
      ancestors.delete(val);
      return;
    }

    // functions, symbols, undefined at the root, etc — not valid JSON, mirror JSON.stringify(undefined)
    yield* write("null");
  }

  yield* walk(value, 0);
  if (current) yield current;
  onProgress?.({ nodesWritten, charsWritten });
}

/**
 * Cheaply counts the array/object/leaf nodes `generateJsonChunks` would visit for `value`,
 * without stringifying anything — use as the denominator for a `nodesWritten`-based progress %.
 * Mirrors `generateJsonChunks`'s traversal exactly (same `toJSON`/circular-reference/undefined
 * handling) so the count lines up with what it'll actually report.
 */
export function countJsonNodes(value: unknown): number {
  const ancestors = new Set<object>();

  function count(val: unknown): number {
    if (val && typeof val === "object" && typeof (val as { toJSON?: unknown }).toJSON === "function") {
      val = (val as { toJSON: () => unknown }).toJSON();
    }

    if (val === null || typeof val !== "object") {
      return 1;
    }

    if (ancestors.has(val)) throw new TypeError("Converting circular structure to JSON");

    if (Array.isArray(val)) {
      if (val.length === 0) return 1;
      ancestors.add(val);
      let total = 1;
      for (const item of val) total += count(item === undefined ? null : item);
      ancestors.delete(val);
      return total;
    }

    const entries = Object.entries(val).filter(([, v]) => v !== undefined);
    if (entries.length === 0) return 1;
    ancestors.add(val);
    let total = 1;
    for (const [, v] of entries) total += count(v);
    ancestors.delete(val);
    return total;
  }

  return count(value);
}

/** Same output as `generateJsonChunks`, collected eagerly into an array. */
export function stringifyToChunks(value: unknown, indentSize = 2, chunkSize = DEFAULT_CHUNK_SIZE): string[] {
  return Array.from(generateJsonChunks(value, indentSize, chunkSize));
}

/**
 * Builds a JSON `Blob` from `value` without going through a single `JSON.stringify` string, so
 * arbitrarily large payloads don't hit the JS engine's max string length. Note this still holds
 * every chunk (i.e. the whole serialized payload) in memory at once — for payloads large enough
 * to risk an out-of-memory crash, prefer `writeJsonToFile` where supported.
 */
export function jsonToBlob(value: unknown, indentSize = 2): Blob {
  return new Blob(stringifyToChunks(value, indentSize), { type: "application/json" });
}

/**
 * Streams `value` as JSON straight to a user-chosen file on disk via the File System Access API,
 * one chunk at a time — the full serialized payload is never held in memory at once, unlike
 * `jsonToBlob`. Only call where `supportsFileSystemWriter()` is true. Rejects with a `DOMException`
 * named "AbortError" if the user cancels the save dialog — callers should treat that as a no-op,
 * not a failure.
 */
export async function writeJsonToFile(value: unknown, suggestedName: string, indentSize = 2): Promise<void> {
  const writable = await pickWritableFile(suggestedName, [
    { description: "JSON", accept: { "application/json": [".json"] } },
  ]);

  try {
    for (const chunk of generateJsonChunks(value, indentSize)) {
      await writable.write(chunk);
    }
  } catch (error) {
    await writable.abort(error);
    throw error;
  }

  await writable.close();
}
